import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ActionIcon, Loader, Popover, Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedValue, useHotkeys } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import { useSearchControllerRun } from '@werkbank/shared';
import { DOC_TYPE_LABELS } from '../lib/format';
import { looksLikeLeaseNumber } from '../lib/unitQuickCreate';
import { UnitQuickCreateDialog } from './UnitQuickCreateDialog';
import { Icon } from './navIcons';

/**
 * Globale Suche direkt im Header (Faruq): kein Modal mehr, sondern ein
 * Inline-Suchfeld mittig in der Topbar. Die Treffer erscheinen als Dropdown
 * unter dem Feld, je Treffer mit seiner Fundstelle (Auftrag, Kunde, Rechnung,
 * Mietvertragsnummer …). Server-Suche über useSearchControllerRun; Strg/⌘+K
 * fokussiert das Feld. Tastaturnavigation (↑/↓/Enter/Esc) bleibt erhalten.
 */

type Category = 'orders' | 'customers' | 'leases' | 'documents';

interface FlatHitBase {
  flatIdx: number;
  label: string;
  sub: string;
  /** Menschlich lesbare Fundstelle (rechts am Treffer). */
  kind: string;
}
type FlatHit =
  | (FlatHitBase & { cat: 'orders'; orderId: string })
  | (FlatHitBase & { cat: 'customers'; customerId: string })
  | (FlatHitBase & { cat: 'leases'; customerId: string; propertyId: string; unitId: string })
  | (FlatHitBase & { cat: 'documents'; documentId: string });

const GROUPS: { cat: Category; title: string }[] = [
  { cat: 'orders', title: 'Aufträge' },
  { cat: 'customers', title: 'Kunden' },
  // Faruq 2026-08-24: „Mietvertrag" verspricht ein Dokument, das es nicht
  // gibt — `leaseNumber` ist ein Feld an der WOHNUNG. Ehrliche Beschriftung.
  { cat: 'leases', title: 'Wohnungen' },
  { cat: 'documents', title: 'Belege' },
];

const MIN_QUERY_LENGTH = 2;

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const SEARCH_KBD = IS_MAC ? '⌘K' : 'Strg K';

export function HeaderSearch() {
  // Kundentest 2026-08 (Faruq): die Kopfzeile trägt nur ein Suchsymbol; das
  // Feld klappt erst beim Klick auf (und schließt wieder, wenn es leer ist).
  const [aufgeklappt, setAufgeklappt] = useState(false);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query.trim(), 200);
  const [highlight, setHighlight] = useState(0);
  const [focused, setFocused] = useState(false);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Strg/⌘+K fokussiert das Suchfeld (überall, auch aus Eingabefeldern).
  useHotkeys(
    [
      [
        'mod+K',
        () => {
          setAufgeklappt(true);
          // Erst nach dem Rendern fokussierbar.
          setTimeout(() => inputRef.current?.focus(), 0);
        },
      ],
    ],
    [],
  );

  const enabled = debounced.length >= MIN_QUERY_LENGTH;
  const search = useSearchControllerRun({ q: debounced }, { query: { enabled } });
  const data = enabled ? search.data : undefined;

  const flat = useMemo<FlatHit[]>(() => {
    const out: FlatHit[] = [];
    let n = 0;
    (data?.orders ?? []).forEach((o) => {
      out.push({
        cat: 'orders',
        flatIdx: n++,
        label: `${o.number} · ${o.title}`,
        sub: o.customerName ?? '',
        kind: 'Auftrag',
        orderId: o.id,
      });
    });
    (data?.customers ?? []).forEach((cu) => {
      out.push({
        cat: 'customers',
        flatIdx: n++,
        label: cu.displayName,
        sub: cu.kind === 'COMPANY' ? 'Firma' : 'Privatkunde',
        kind: 'Kunde',
        customerId: cu.id,
      });
    });
    (data?.leases ?? []).forEach((l) => {
      out.push({
        cat: 'leases',
        flatIdx: n++,
        label: l.leaseNumber,
        sub: [l.unitLabel, l.propertyAddress, l.customerName].filter(Boolean).join(' · '),
        kind: 'Mietvertragsnr.',
        customerId: l.customerId,
        propertyId: l.propertyId,
        unitId: l.unitId,
      });
    });
    (data?.documents ?? []).forEach((d) => {
      out.push({
        cat: 'documents',
        flatIdx: n++,
        label: `${d.number} · ${d.customerName ?? ''}`.replace(/ · $/, ''),
        sub: d.customerName ?? '',
        kind: DOC_TYPE_LABELS[d.type] ?? 'Beleg',
        documentId: d.id,
      });
    });
    return out;
  }, [data]);

  useEffect(() => setHighlight(0), [flat]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({
      block: 'nearest',
    });
  }, [highlight]);

  const close = () => {
    setQuery('');
    setHighlight(0);
    inputRef.current?.blur();
  };

  const go = (hit: FlatHit) => {
    switch (hit.cat) {
      case 'orders':
        void navigate({ to: '/orders/$orderId', params: { orderId: hit.orderId } });
        break;
      case 'customers':
        void navigate({ to: '/customers/$customerId', params: { customerId: hit.customerId } });
        break;
      // Der Wohnungstreffer landete bisher im selben Zweig wie ein Kundentreffer
      // — also auf der Kundenuebersicht, und die Adresse aus der Trefferzeile
      // war weg (Faruq 2026-08-24). Jetzt reisen Gebaeude und Wohnung mit: die
      // Akte klappt das Gebaeude auf und hebt die Zeile hervor. Eine eigene
      // Wohnungsseite gibt es (noch) nicht.
      case 'leases':
        void navigate({
          to: '/customers/$customerId',
          params: { customerId: hit.customerId },
          search: { propertyId: hit.propertyId, unitId: hit.unitId },
        });
        break;
      case 'documents':
        void navigate({ to: '/documents/$documentId', params: { documentId: hit.documentId } });
        break;
    }
    close();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (flat.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = flat[highlight];
      if (hit) go(hit);
    }
  };

  const hasResults = flat.length > 0;
  const showCreateUnit =
    enabled &&
    !search.isFetching &&
    looksLikeLeaseNumber(debounced) &&
    (data?.leases?.length ?? 0) === 0;

  // Dropdown offen, sobald das Feld fokussiert ist und etwas getippt wurde.
  const open = focused && query.trim().length > 0;

  if (!aufgeklappt) {
    return (
      <ActionIcon
        variant="subtle"
        color="gray"
        title={`Suchen (${SEARCH_KBD})`}
        aria-label="Suchen"
        data-testid="global-search-open"
        onClick={() => {
          setAufgeklappt(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <Icon name="search" size={18} />
      </ActionIcon>
    );
  }

  return (
    <>
      <Popover
        opened={open}
        width="target"
        position="bottom"
        offset={6}
        withinPortal
        trapFocus={false}
        closeOnEscape={false}
        shadow="md"
        radius="md"
      >
        <Popover.Target>
          <TextInput
            // Aufgeklappt bewusst schmal (rund halb so breit wie zuvor, Faruq).
            w="min(280px, 32vw)"
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              // Leer verlassen ⇒ zurück zum Symbol; mit Eingabe bleibt das
              // Feld stehen, damit der Treffer anklickbar ist.
              if (!query.trim()) setAufgeklappt(false);
            }}
            placeholder="Aufträge, Kunden, Mietvertragsnummern, Belege…"
            aria-label="Globale Suche"
            data-testid="global-search-input"
            leftSection={<Icon name="search" size={16} />}
            rightSection={!query ? <span className="wb-kbd-chip">{SEARCH_KBD}</span> : undefined}
            rightSectionWidth={query ? undefined : 64}
          />
        </Popover.Target>

        <Popover.Dropdown
          p={0}
          // Klick auf ein Ergebnis darf das Feld NICHT vorher unfokussieren.
          onMouseDown={(e) => e.preventDefault()}
        >
          <div ref={listRef} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {!enabled && (
              <Text size="sm" c="dimmed" p="md" data-testid="global-search-hint">
                Mindestens zwei Zeichen eingeben, um zu suchen.
              </Text>
            )}
            {enabled && search.isFetching && !hasResults && (
              <Stack align="center" py="md">
                <Loader size="sm" />
              </Stack>
            )}
            {enabled && !search.isFetching && !hasResults && !showCreateUnit && (
              <Text size="sm" c="dimmed" p="md" data-testid="global-search-empty">
                Keine Treffer für „{debounced}“.
              </Text>
            )}

            {hasResults &&
              GROUPS.map(({ cat, title }) => {
                const hits = flat.filter((h) => h.cat === cat);
                if (hits.length === 0) return null;
                return (
                  <div key={cat}>
                    <Text
                      size="xs"
                      fw={700}
                      c="dimmed"
                      data-testid={`search-group-${cat}`}
                      style={{
                        padding: 'var(--wb-space-sm) var(--wb-space-md) var(--wb-space-xs)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {title}
                    </Text>
                    {hits.map((h) => {
                      const active = h.flatIdx === highlight;
                      return (
                        <button
                          key={`${cat}-${h.flatIdx}`}
                          type="button"
                          data-testid={`search-result-${cat}-${h.flatIdx}`}
                          data-active={active ? 'true' : 'false'}
                          onMouseEnter={() => setHighlight(h.flatIdx)}
                          onClick={() => go(h)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--wb-space-md)',
                            width: '100%',
                            textAlign: 'left',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 'var(--wb-space-xs) var(--wb-space-md)',
                            background: active ? 'var(--wb-surface-hover)' : 'transparent',
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Text size="sm" fw={500} truncate>
                              {h.label}
                            </Text>
                            {h.sub && (
                              <Text size="xs" c="dimmed" truncate>
                                {h.sub}
                              </Text>
                            )}
                          </div>
                          <span className="wb-search-kind">{h.kind}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}

            {showCreateUnit && (
              <button
                type="button"
                data-testid="search-create-unit"
                onClick={() => setUnitDialogOpen(true)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  border: 'none',
                  borderTop: '1px solid var(--wb-surface-hairline)',
                  padding: 'var(--wb-space-sm) var(--wb-space-md)',
                  background: 'transparent',
                }}
              >
                <Text size="sm" fw={600} c="var(--mantine-primary-color-filled)">
                  „{debounced}“ nicht gefunden — jetzt als neue Einheit anlegen?
                </Text>
                <Text size="xs" c="dimmed">
                  Schnellanlage einer Mietereinheit mit dieser Mietvertragsnummer
                </Text>
              </button>
            )}
          </div>
        </Popover.Dropdown>
      </Popover>

      <UnitQuickCreateDialog
        opened={unitDialogOpen}
        onClose={() => setUnitDialogOpen(false)}
        initialLeaseNumber={debounced}
        onCreated={() => {
          setUnitDialogOpen(false);
          close();
        }}
      />
    </>
  );
}
